import AppDispatcher from '../core/AppDispatcher.jsx';
import {EventEmitter} from 'events';
import _ from 'lodash';
import {ActionTypes, ClientIdentificationModes} from '../constants/ApplicationConstants.js';
import {DocumentModels, DocumentTypes as DOCUMENT_TYPES, DefaultDocType, ReactKeyOther} from '../constants/IDVerificationConstants.js';
import ContentService from '../services/ContentService.js';
import AccountTypeConstants from '../constants/AccountTypeConstants';
import YourDetailsConstants from '../constants/YourDetailsConstants';
import DownloadConstants from '../constants/DownloadConstants';
import DownloadFormsHelper from '../utils/DownloadFormsHelper.js';
import WorkItemHelper from '../utils/WorkItemHelper.js';
import IdVerificationHelper from '../utils/IdVerificationHelper.js';
import IdVerificationFieldValidationHelper from '../utils/IdVerificationFieldValidationHelper.js';
import ApplicationHelper from '../utils/ApplicationHelper.js';

const CHANGE_EVENT = 'change';
const AccountTypeActionTypes = AccountTypeConstants.ActionTypes;
const YourDetailsActionTypes = YourDetailsConstants.ActionTypes;

let _formModel = {},
    _product = null,
    _showMoreMenu = false,
    _accountNumber = '',
    _hasSubmitFailed = false;

let _VerificationDocumentInEdit = {};

let _IDVerificationModel = null;

let _workItem = null; 

let _titleList = {},
    _accountType = '',
    _showDownloadMenu = false,
    _formsToDownload = [],
    _downloadInProgress = false,
    _selectedForms = [],
    _readyForDownload = false,
    _downloadedForms = '',
    _formDownloaded = true,
    _downloadFormsFailed = false,
    _commSeeKycVerifiedDate = null;
    
function setReviewData(formModel, product) {
    _formModel = _.cloneDeep(formModel);
    _product = _.cloneDeep(product);
    _accountType = _.cloneDeep(formModel.form.accountType);
    initIDVerificationModel(formModel, false);
    initIDVerificationInEdit();
}

function retrieveApplication(formModel) {
    _formModel = _.cloneDeep(formModel);
    initIDVerificationModel(formModel, true);

    _.each(_IDVerificationModel.investorList, investor => {
        updateInvestorValidStatus(investor.investorId);
    });

    _formModel.form.idVerification = _.cloneDeep(_IDVerificationModel);
}

function initIDVerificationModel(formModel, isRetreive) {
    if (formModel.form.idVerification === null || isRetreive) {
        _IDVerificationModel = IdVerificationHelper.flattenVerificationDocuments(formModel, _titleList, isRetreive);
    } else {
        _IDVerificationModel = _.cloneDeep(formModel.form.idVerification);
    }
}

function initIDVerificationInEdit() {
    _VerificationDocumentInEdit = {};
    _.each(_IDVerificationModel.investorList, (investor) => {
        setVerificationDocumentInEdit(investor.investorId, investor.kycFlag);
    });
}

function resetForm() {
    _IDVerificationModel = null;
    _VerificationDocumentInEdit = {};
}

function setIdentificationMode(data) {
    _commSeeKycVerifiedDate = data.commSeeKycVerifiedDate;
    _formModel = IdVerificationHelper.getUpdateFormModelWithKycTypeToUpdate(_formModel, data.kycTypeToUpdate);
}

function createNewVerficiationDocument(reactKey, investorId, investorKycFlag) {
    if (investorKycFlag) {
        return null;
    } 

    const data = _.clone(DocumentModels, true);
    let newDocument = _.find(data, {
        reactKey: reactKey
    });

    _.assign(newDocument, {
        investorId: investorId,
        id: _.uniqueId('investor' + investorId + '_'),
        isValid: false
    });

    _.each(newDocument.meta, f => {
        if (_.has(f, 'defaultValue') && f.defaultValue) {
            _.assign(f, { value: f.defaultValue, isValid: true });
        }
    });

    return newDocument;
}

function removeIDVerificationModel(id, investorId) {
    _.remove(_IDVerificationModel.investorList[investorId].verificationDocuments, {id: id});
    updateInvestorValidStatus(investorId);
    _formModel.form.idVerification = _.cloneDeep(_IDVerificationModel);
}

function updateInvestorValidStatus(investorId) {
    updateUserRequiredDocs(investorId);

    let investorKycFlag = _IDVerificationModel.investorList[investorId].kycFlag;
    setVerificationDocumentInEdit(investorId, investorKycFlag);

    _IDVerificationModel.investorList[investorId].isValid = _.every( _IDVerificationModel.investorList[investorId].verificationDocuments, {isValid: true});
}

function getInvestorDocTypeStatus(investorId, excludeEditDocId = null) {
    const primaryPhoto = _.filter(_IDVerificationModel.investorList[investorId].verificationDocuments, (doc) => {
        return doc.documentType === DOCUMENT_TYPES.PRIMARY_PHOTOGRAPHIC && doc.id !== excludeEditDocId;
    });
    const primaryWithoutPhoto = _.filter(_IDVerificationModel.investorList[investorId].verificationDocuments, (doc) => {
        return doc.documentType === DOCUMENT_TYPES.PRIMARY_NON_PHOTOGRAPHIC && doc.id !== excludeEditDocId;
    });
    const secondary = _.filter(_IDVerificationModel.investorList[investorId].verificationDocuments, (doc) => {
        return doc.documentType === DOCUMENT_TYPES.SECONDARY && doc.id !== excludeEditDocId
    });
    return {
        primaryPhotoCount: primaryPhoto.length,
        primaryWithoutPhotoCount: primaryWithoutPhoto.length,
        secondaryCount: secondary.length
    }
}

function updateUserRequiredDocs(investorId) {
    let status = getInvestorDocTypeStatus(investorId);

    let hasAll = false;
    let hasNonPrimaryOnly = false;
    let hasSecondaryOnly = false;

    if (status.primaryPhotoCount >= 1) {
        hasAll = true;
    } else if (status.primaryWithoutPhotoCount >= 2) {
        hasAll = true;
    } else if ((status.primaryWithoutPhotoCount >= 1) && (status.secondaryCount >= 1)) {
        hasAll = true;
    } else if (status.primaryWithoutPhotoCount >= 1) {
        hasNonPrimaryOnly = true;
    } else if (status.secondaryCount >= 1) {
        hasSecondaryOnly = true;
    }
    _IDVerificationModel.investorList[investorId].hasRequiredDocs = hasAll;
    _IDVerificationModel.investorList[investorId].hasNonPrimaryOnly = hasNonPrimaryOnly;
    _IDVerificationModel.investorList[investorId].hasSecondaryOnly = hasSecondaryOnly;
}

function changeIDVerificationType(docId, investorId, docTypeKey) {
    let newDocType = _.cloneDeep(_.find(DocumentModels, {reactKey: docTypeKey}));

    _.each(newDocType.meta, f => {
        if (_.has(f, 'defaultValue') && f.defaultValue) {
            _.assign(f, { value: f.defaultValue, isValid: true });
        } 
    });
    
    _VerificationDocumentInEdit[investorId] = newDocType;

    _.assign(_VerificationDocumentInEdit[investorId], {
        investorId: investorId,
        id: docId,
        isValid: false
    });
}

function updateFormModelWorkItem(workItem) {
    if (_formModel.form) {
        _formModel.form.workItem = WorkItemHelper.getUpdatedFormModel(_formModel, workItem);
    }
}

function updateYourDetailsForSave() {
    if (_formModel.form) {
        _formModel.form.yourDetails = 
            IdVerificationHelper.formatVerificationDocuments(_formModel.productId, _formModel.form.accountType, _formModel.form.yourDetails, _formModel.form.idVerification);
    }
}

function updateIDVerificationInput(docId, investorId, formInputValue, formInputName) {
    let docTypeKey;

    let investorVerificationDocumentInEdit = _.has(_VerificationDocumentInEdit , investorId)
        ? _VerificationDocumentInEdit[investorId]: null;

    if (investorVerificationDocumentInEdit &&  investorVerificationDocumentInEdit.id !== docId) {
        const existingDoc = _.find(_IDVerificationModel.investorList[investorId].verificationDocuments, { id: docId });
        docTypeKey = existingDoc.reactKey;
        _.assign(_VerificationDocumentInEdit[investorId], {investorId: investorId, id: docId, reactKey: docTypeKey});
    } else {
        docTypeKey = investorVerificationDocumentInEdit.reactKey;
    }

    // individual input value check
    const inputValidationResult = IdVerificationFieldValidationHelper
        .getInputValidationResult(docTypeKey, docId, investorId, formInputValue, formInputName);

    if (!_.find(investorVerificationDocumentInEdit.meta, { model: formInputName })) {
        const docModel = _.cloneDeep(_.find(DocumentModels, { reactKey: docTypeKey }));
        const formInputMeta = _.find(docModel.meta, { model: formInputName });
        investorVerificationDocumentInEdit.meta.push(formInputMeta);
    }

    let editMeta = _.find(investorVerificationDocumentInEdit.meta, { model: formInputName });
    const error = (inputValidationResult.errorList && inputValidationResult.errorList.length > 0 )? inputValidationResult.errorList[0]: null;
    _.assign(editMeta, {value: formInputValue, isValid: inputValidationResult.isValid, errorList: error});

    // cross input fields values check
    _.each(investorVerificationDocumentInEdit.meta, s => {
        if (_.has(s, 'crossFieldValidation')) {
            const t = _.find(investorVerificationDocumentInEdit.meta, { model: s.crossFieldValidationTarget });
            if (t && t.isValid) {
                const sourceValidationresult = IdVerificationFieldValidationHelper
                    .getInputValidationResult(docTypeKey, docId, investorId, s.value ? s.value: '', s.model);
                if (sourceValidationresult.isValid) {
                    const crossInputValidationResult = IdVerificationFieldValidationHelper
                        .getCrossInputValidationResult(s.label, s.value, t.label, t.value, s.crossFieldValidation);
                    if (!crossInputValidationResult.isValid) {
                        const error = (crossInputValidationResult.errorList && crossInputValidationResult.errorList.length > 0 )
                            ? crossInputValidationResult.errorList[0]: null;
                        _.assign(s, {isValid: false, errorList: error});
                    } else {
                        _.assign(s, { isValid: true, errorList: null });
                    }
                }
            }
        }
    });

    // check whether the doc in edit has all mandatory fields valid
    const isAllFieldsValidated = _.every(investorVerificationDocumentInEdit.meta, f => {
            return (f.mandatory === true && f.isValid === true) || (f.mandatory === false && f.isValid !== false);
        });
    _.assign(investorVerificationDocumentInEdit,{ isValid: isAllFieldsValidated });
}

function startEditIDVerificationDocument(docId, investorId) {
    let verificationDocumentInEdit = _.find(_IDVerificationModel.investorList[investorId].verificationDocuments, { id: docId });
    _.assign(verificationDocumentInEdit, { isEditing: true });
    _VerificationDocumentInEdit[investorId] = _.cloneDeep(verificationDocumentInEdit);

    // this is needed as the form may be retrieved and no validation is done (isValid flag is not updated after retrieve)
    if (verificationDocumentInEdit) {
        _.each(verificationDocumentInEdit.meta, meta => {
            updateIDVerificationInput(docId, investorId, meta.value, meta.model);
        })
    }
}

function cancelEditIDVerificationDocument(docId, investorId) {
    let investorVerificationDocumentInEdit = _.has(_VerificationDocumentInEdit, investorId)? _VerificationDocumentInEdit[investorId]: null;
    if (investorVerificationDocumentInEdit &&  investorVerificationDocumentInEdit.id === docId) {
        let investorKycFlag = _IDVerificationModel.investorList[investorId].kycFlag;
        setVerificationDocumentInEdit(investorId, investorKycFlag);
    }
}

function finishEditIDVerificationDocument(docId, investorId) {
    let investorVerificationDocumentInEdit = _.has(_VerificationDocumentInEdit, investorId)? _VerificationDocumentInEdit[investorId]: null;
    if (investorVerificationDocumentInEdit && investorVerificationDocumentInEdit.isValid) {
        let currentIdx = _.findIndex(_IDVerificationModel.investorList[investorId].verificationDocuments, { id: docId });
        let verificationDocumentInEdit = _.assign(_VerificationDocumentInEdit[investorId], { isEditing: false });
        if (currentIdx > -1) {
            _IDVerificationModel.investorList[investorId].verificationDocuments[currentIdx] = _
                .cloneDeep(verificationDocumentInEdit);
        } else {
            _IDVerificationModel.investorList[investorId].verificationDocuments
                .push(_.cloneDeep(verificationDocumentInEdit));
        }
    }

    updateInvestorValidStatus(investorId);
    _formModel.form.idVerification = _.cloneDeep(_IDVerificationModel);
}

function agreeIdVerificationForm() {
    setWorkItem();
    _formModel.form.idVerification = _.cloneDeep(_IDVerificationModel);
    _formModel.form.yourDetails = 
        IdVerificationHelper.formatVerificationDocuments(_formModel.productId, _formModel.form.accountType, _formModel.form.yourDetails, _formModel.form.idVerification, true);
    _hasSubmitFailed = false;
}

function setVerificationDocumentInEdit(investorId, investorKycFlag) {
    if (investorKycFlag) {
        _VerificationDocumentInEdit[investorId] = null;
    } else {
        let status = getInvestorDocTypeStatus(investorId);
        if (status.primaryPhotoCount >= 1) {
            _VerificationDocumentInEdit[investorId] = null;
        } else if (status.primaryWithoutPhotoCount >= 2) {
            _VerificationDocumentInEdit[investorId] = null;
        } else if (status.primaryWithoutPhotoCount >= 1 && status.secondaryCount >= 1) {
            _VerificationDocumentInEdit[investorId] = null;
        } else if (status.primaryWithoutPhotoCount >= 1 || status.secondaryCount >= 1) {
            _VerificationDocumentInEdit[investorId] = createNewVerficiationDocument(ReactKeyOther, investorId, investorKycFlag)
        } else {
            _VerificationDocumentInEdit[investorId] = createNewVerficiationDocument(DefaultDocType, investorId, investorKycFlag);
        }
    }
}

function getFormValidations(investorId, docId) {
    let investorVerificationDocumentInEdit = _.has(_VerificationDocumentInEdit, investorId)? _VerificationDocumentInEdit[investorId]: null;

    if (!investorVerificationDocumentInEdit || investorVerificationDocumentInEdit.docId !== docId) {
        return [];
    }
    
    const docMeta = investorVerificationDocumentInEdit.meta;
    let validations = [];

    _.each(docMeta, (field) => {
        // handle default fields and optional fields
        if (!field.value && field.defaultValue) {
            validations.push(true);
        } else if (!field.value && !field.mandatory) {
            validations.push(true);
        } else {
            validations.push(field.isValid);
        }
    });

    return validations;
}

function setWorkItem() {
    const investorList = _.get(_IDVerificationModel, 'investorList', []);
    if (investorList.length > 0 && investorList[0].kycFlag && _commSeeKycVerifiedDate !== null) {
        _.assign(investorList[0],
        {
            cif: ApplicationHelper.getClientCif(_formModel),
            commSeeKycVerifiedDate: _commSeeKycVerifiedDate
        });
    }

    let backFile = WorkItemHelper.getIdVerificationBackFile(investorList);

    if (_workItem === null) {
        _workItem = {};
    }

    _.assign(_workItem, { idVerificationBackFile: backFile});
}

function setAccountNumber(accountNumber){
    _hasSubmitFailed = false;
    _accountNumber = accountNumber;
}

function setAccountType(accountType) {
    _accountType = accountType;
    _showDownloadMenu = false;
    _formsToDownload = [];
}
//TODO:: REFACTOR THIS INTO A DOWNLOAD MENU STORE
function toggleDownloadMenu(showDownloadMenu) {
    _showDownloadMenu = showDownloadMenu;
    if (showDownloadMenu && _product && _product.value) {
        _formsToDownload = DownloadFormsHelper.getFormsList(_product.value, _accountType);
    } else {
        _formsToDownload = [];
    }
    _selectedForms = [];
    _downloadInProgress = false;
    _downloadFormsFailed = false;
}

function updateFormsToDownload(index, checked) {
    if (_formsToDownload[index]) {
        _formsToDownload[index].checked = checked;
    }
}

function getSelectedForms() {
    if (_formsToDownload && _formsToDownload.length > 0) {
        _selectedForms = DownloadFormsHelper.getSelectedForms(_formsToDownload);
        _downloadInProgress = true;
    }
}

function receiveDownloadedApplicationForm(data) {
    if (data.attachments && data.attachments.length > 0) {
        let url = DownloadConstants.Base64String;
        _downloadedForms = url.replace('{contentType}', data.attachments[0].contentType).replace('{base64}', data.attachments[0].body);
    }
    _downloadFormsFailed = false;
    _formDownloaded = false;
    _downloadInProgress = false;
    _showDownloadMenu = false;
    _formsToDownload = [];
    _selectedForms = [];
}
//TODO:: REFACTOR THIS INTO A DOWNLOAD MENU STORE

class IDVerificationStoreClass extends EventEmitter {

    addChangeListener(cb) {
        this.on(CHANGE_EVENT, cb);
    }

    removeChangeListener(cb) {
        this.removeListener(CHANGE_EVENT, cb);
    }

    emitChange() {
        return this.emit(CHANGE_EVENT);
    }

    getContent() {
        return ContentService.getContent().IDVerification;
    }

    getDocument(investorId, id) {
        const doc = _.find(_IDVerificationModel.investorList[investorId].verificationDocuments, {
            id: id
        });

        return doc;
    }

    getAllValidations() {
        let allRequiredDocs = true;

        _.each(_IDVerificationModel.investorList, (investor) => {
            if (!investor.kycFlag) {
                if (!investor.hasRequiredDocs) {
                    allRequiredDocs = false;
                }
                if (!investor.isValid) {
                    allRequiredDocs = false;
                }
            }
        });

        return allRequiredDocs;
    }

    getFieldValidations(investorId, docId) {
        return getFormValidations(investorId, docId);
    }

    getOptions() {
        let options = [];
        _.each(_IDVerificationModel.investorList, (investor, investorId) => {
            
            let investorVerificationDocumentInEdit = _.has(_VerificationDocumentInEdit, investor.investorId)
                ? _VerificationDocumentInEdit[investor.investorId]: null;

            options[investorId] = [];
            const list = _.clone(DocumentModels, true);

            let status = getInvestorDocTypeStatus(investorId, investorVerificationDocumentInEdit ? investorVerificationDocumentInEdit.id: null);
           
            _.each(_.sortBy(list, 'type'), (o) => {
                let isOptionEditing = investorVerificationDocumentInEdit !== null &&
                    o.reactKey === investorVerificationDocumentInEdit.reactKey;
                let isOptionAdded = _.some(investor.verificationDocuments, {reactKey: o.reactKey});
                
                let option = o;
                option.label = o.type;
                option.value = o.reactKey;

                if (isOptionEditing) {
                    options[investorId].push(option);
                } else if (status.primaryPhotoCount === 0 && status.primaryWithoutPhotoCount === 0 && status.secondaryCount === 0) {
                    options[investorId].push(option);
                } else if (status.primaryPhotoCount === 0 && status.primaryWithoutPhotoCount >= 1 && status.secondaryCount === 0) {
                    if (!isOptionAdded && o.documentType !== DOCUMENT_TYPES.PRIMARY_PHOTOGRAPHIC) {
                        options[investorId].push(option);
                    }
                } else if (status.primaryPhotoCount === 0 && status.primaryWithoutPhotoCount === 0 && status.secondaryCount >= 1) {
                    if (!isOptionAdded &&
                        o.documentType !== DOCUMENT_TYPES.PRIMARY_PHOTOGRAPHIC &&
                        o.documentType !== DOCUMENT_TYPES.SECONDARY) {
                        options[investorId].push(option);
                    }
                }
            });
        });

        return options;
    }

    getIDVerificationModel() {
        return _IDVerificationModel;
    }

    getInvestorList() {
        let investorList = _.cloneDeep(_IDVerificationModel.investorList);
        _.each(investorList, investor => {

            let addedVerificationDocumentInEdit = false;
            let investorVerificationDocumentInEdit = _.has(_VerificationDocumentInEdit, investor.investorId)
                ? _VerificationDocumentInEdit[investor.investorId]: null;

            _.each(investor.verificationDocuments, (doc, docIdx) => {
                let isEditing = investorVerificationDocumentInEdit && doc.id === investorVerificationDocumentInEdit.id;
                if (isEditing) {
                    addedVerificationDocumentInEdit = true;
                    investor.verificationDocuments[docIdx] = _.cloneDeep(investorVerificationDocumentInEdit);
                }
                investor.verificationDocuments[docIdx]['isEditing'] = isEditing;
            })

            if (!addedVerificationDocumentInEdit && investorVerificationDocumentInEdit) {
                let verificationDocumentInEdit = _.assign(_VerificationDocumentInEdit[investor.investorId], { isEditing: true });
                investor.verificationDocuments.push(verificationDocumentInEdit);
            }
        });
        return investorList;
    }

    getIdVerificationBackFile() {
        return (_workItem && _workItem.idVerificationBackFile) ? _workItem.idVerificationBackFile : null;
    }

    getProduct() {
        return _product;
    }

    getShowHideMore() {
        return _showMoreMenu;
    }

    getFormModel() {
        return _formModel;
    }

    getAccountNumber(){
        return _accountNumber;
    }

    getHasSubmitFailed() {
        return _hasSubmitFailed;
    }

    getTitles(){
        return _titleList;
    }

    getAccountType() {
        return _accountType;
    }
    getShowDownloadMenu() {
        return _showDownloadMenu;
    }
    getFormsToDownload() {
        return _formsToDownload;
    }
    getEnableDownloadButton() {
        let checked = _.where(_formsToDownload, {'checked':true});
        return checked && checked.length > 0;
    }
    getDownloadInProgress() {
        return _downloadInProgress;
    }
    getSelectedForms() {
        return _selectedForms;
    }
    getReadyForDownload() {
        return _readyForDownload === true && _downloadInProgress === true;
    }
    getDownloadedForms() {
        return _downloadedForms;
    }
    getIsFormsDownloaded() {
        return _formDownloaded;
    }
    getDownloadFormsFailed() {
        if (_downloadInProgress) {
            return false;
        }
        return _downloadFormsFailed;
    }
}

const IDVerificationStore = new IDVerificationStoreClass();

IDVerificationStore.dispatchToken = AppDispatcher.register((action) => {
    switch (action.actionType) {
        case YourDetailsActionTypes.ADD_ADDITIONAL_INVESTORS:
        case YourDetailsActionTypes.DELETE_TRUSTEE:
        case YourDetailsActionTypes.ADD_TRUSTEE:
            resetForm();
            break;
        case ActionTypes.APPLICATION_SAVE_REQUEST:
        case ActionTypes.APPLICATION_SAVE_STARTING: 
            updateYourDetailsForSave();
            break;
        case ActionTypes.APPLICATION_RETRIEVE_SUCCESS:
            retrieveApplication(action.applicationData);
            break;
        case ActionTypes.APPLICATION_SAVE_SUCCESS:
            _formModel.id = action.applicationId;
            if (action.wipId) {
                _formModel.wipId = action.wipId;
            }
            break;
        case ActionTypes.APPLICATION_SUBMIT_SUCCESS:
            setAccountNumber(action.data.accountNumber);
            break;
        case ActionTypes.APPLICATION_SUBMIT_FAILED:
            _hasSubmitFailed = true;
            _formModel.submitDate = '';
            break;
        case ActionTypes.CLIENT_IDENTIFICATION_MODE_LOADED:
            setIdentificationMode(action.data);
            if (action.data.identificationMode === ClientIdentificationModes.E_FORM) {
                initIDVerificationInEdit();
            }
            if (action.data.ignoreCommSeeKyc) {
                _formModel = IdVerificationHelper.clearKycInfoForNonCfpAdviser(_formModel, _titleList);
                initIDVerificationModel(_formModel, false);
                initIDVerificationInEdit();
            }
            break;
        case ActionTypes.REMOVE_IDVERIFICATION_DOCUMENT:
            removeIDVerificationModel(action.id, action.investorId);
            break;
        case ActionTypes.CHANGE_IDVERIFICATION_DOCUMENT:
            changeIDVerificationType(action.docId, action.investorId, action.docTypeKey);
            break;
        case ActionTypes.UPDATE_IDVERIFICATION_DOCUMENT:
            updateIDVerificationInput(action.docId, action.investorId, action.formInputValue, action.formInputName);
            break;
        case ActionTypes.START_EDIT_IDVERIFICATION_DOCUMENT:
            startEditIDVerificationDocument(action.docId, action.investorId);
            break;
        case ActionTypes.CANCEL_EDIT_IDVERIFICATION_DOCUMENT:
            cancelEditIDVerificationDocument(action.docId, action.investorId);
            break;
        case ActionTypes.FINISH_EDIT_IDVERIFICATION_DOCUMENT:
            finishEditIDVerificationDocument(action.docId, action.investorId);
            break;
        case ActionTypes.GET_FORM_FIELD_VALIDATIONS:
            getFormValidations(action.investorId, action.id);
            break;
        case ActionTypes.REVIEW_APPLICATION:
            setReviewData(action.formModel, action.product);
            break;
        case ActionTypes.AGREE_IDVERIFICATION_FORM:
            agreeIdVerificationForm();
            break;
        case ActionTypes.UPDATE_WORKITEM:
            updateFormModelWorkItem(action.workItem);
            break;
        case ActionTypes.SHOW_MORE_MENU:
            _showMoreMenu = action.showMoreMenu;
            break;
        case ActionTypes.LOAD_TITLE_LIST:
            _titleList = action.titles;
            break;
        case AccountTypeActionTypes.SELECT_ACCOUNT_TYPE:
            setAccountType(action.accountType);
            resetForm();
            break;
        case ActionTypes.SHOW_DOWNLOAD_MENU:
            toggleDownloadMenu(action.showDownloadMenu);
            break;
        case ActionTypes.UPDATE_FORMS_TO_DOWNLOAD:
            updateFormsToDownload(action.index, action.checked);
            break;
        case ActionTypes.PREPARE_FORMS_TO_DOWNLOAD:
            getSelectedForms();
            break;
        case ActionTypes.APPLICATION_DOWNLOAD_REQUEST:
            _readyForDownload = true;
            break;
        case ActionTypes.START_DOWNLOADING_FORMS:
            _readyForDownload = false;
            break;
        case ActionTypes.DOWNLOAD_FORMS_SUCCESS:
            receiveDownloadedApplicationForm(action.data);
            break;
        case ActionTypes.CLEAR_DOWNLOADED_FORMS:
            _downloadedForms = undefined;
            _formDownloaded = true;
            _selectedForms = [];
            break;
        case ActionTypes.DOWNLOAD_FORMS_FAILED:
            _downloadInProgress = false;
            _formDownloaded = true;
            _downloadFormsFailed = true;
            break;
        default:
            return true;
    }

    IDVerificationStore.emitChange();
    return true;
});

export default IDVerificationStore;


// WEBPACK FOOTER //
// C:/GH/1106/advice.noa.origination/~/eslint-loader!./stores/IDVerificationStore.js